<template>
    <v-row justify="center">
        <v-dialog v-model="dialog" scrollable persistent max-width="1000px">
            <v-card>
                <v-alert class="mb-0" v-if="error" tile type="error">{{
                    error
                }}</v-alert>
                <v-card-title class="primary white--text">
                    {{ formTitle }}
                    <v-spacer />
                    <!-- <span class="subtitle-2 primary--text">{{ fullName }}</span> -->
                    <v-switch
                        v-model="editGrowthTemplateInWeeks"
                        label="Edit in weeks"
                        dark
                        hide-details
                        class="ma-0"
                    ></v-switch>
                </v-card-title>
                <v-card-text>
                    <v-progress-linear
                        color="primary"
                        absolute
                        :active="isLoadingPlantGrowthTemplate"
                        :indeterminate="isLoadingPlantGrowthTemplate"
                    ></v-progress-linear>
                    <v-form ref="form" class="pt-5" v-model="isFormValid">
                        <v-row>
                            <!-- PotSzie -->
                            <v-col cols="12" md="3">
                                <v-autocomplete
                                    v-model="editedItem.potSizeNodeId"
                                    :items="sortedPotSizes"
                                    item-value="node.id"
                                    :loading="isLoadingPotSizes"
                                    :rules="[rules.required]"
                                    label="Select Pot Size*"
                                    class="subtitle-2"
                                    outlined
                                >
                                    <template slot="item" slot-scope="{ item }"
                                        >{{ item.node.potSizeCm }} cm</template
                                    >
                                    <template
                                        slot="selection"
                                        slot-scope="{ item }"
                                        >{{ item.node.potSizeCm }} cm</template
                                    >
                                </v-autocomplete>
                            </v-col>

                            <!-- Season Effect Months -->
                            <v-col cols="12" md="9">
                                <v-spacer />
                                <v-row class="align-center">
                                    <div class="pl-2 pr-1 pb-1">
                                        <p
                                            class="mt-n2 mb-0 caption font-weight-medium"
                                        >
                                            Select Months*
                                            <span
                                                v-if="
                                                    this.editedItem
                                                        .seasonEffectMonths
                                                        .length == 0
                                                "
                                                class="error--text"
                                                >(Please select months!)</span
                                            >
                                        </p>
                                        <v-chip-group
                                            v-model="
                                                editedItem.seasonEffectMonths
                                            "
                                            active-class="primary dark"
                                            multiple
                                            column
                                        >
                                            <v-chip
                                                label
                                                v-for="month in monthsChoices"
                                                :key="month"
                                                :value="month"
                                                tabindex="-1"
                                                >{{ month }}</v-chip
                                            >
                                        </v-chip-group>
                                    </div>
                                    <!-- Reverse selected months -->
                                    <v-btn
                                        color="primary"
                                        outlined
                                        rounded
                                        class="mx-3 mt-1"
                                        @click="reverseSelectedMonths"
                                        >Reverse</v-btn
                                    >
                                </v-row>
                            </v-col>

                            <!-- Name -->
                            <v-col
                                cols="12"
                                :md="editGrowthTemplateInWeeks ? 4 : 6"
                            >
                                <v-text-field
                                    v-model="editedItem.name"
                                    :counter="50"
                                    :rules="[rules.required]"
                                    label="Label*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>

                            <!-- #Phases -->
                            <v-col
                                cols="6"
                                :md="editGrowthTemplateInWeeks ? 2 : 3"
                            >
                                <v-select
                                    v-model="editedItem.numPhases"
                                    :items="numPhasesChoices"
                                    :rules="[rules.required]"
                                    label="# Phases"
                                    outlined
                                    :menu-props="{ offsetY: true }"
                                    class="subtitle-2"
                                ></v-select>
                            </v-col>

                            <!-- Max Sales Weeks -->
                            <v-col
                                cols="6"
                                md="3"
                                v-if="editGrowthTemplateInWeeks"
                            >
                                <v-select
                                    v-model="editedItem.maxSalesWeeks"
                                    :items="numSaleWeeksChoices"
                                    label="Max Sale Weeks*"
                                    outlined
                                    :menu-props="{ offsetY: true }"
                                    class="subtitle-2"
                                ></v-select>
                            </v-col>
                            <!-- Max Sales Days -->
                            <v-col cols="6" md="3">
                                <v-select
                                    v-model="editedItem.maxSalesDays"
                                    :items="numSaleDaysChoices"
                                    :rules="[rules.required]"
                                    label="Max Sale Days*"
                                    outlined
                                    :disabled="editGrowthTemplateInWeeks"
                                    :menu-props="{ offsetY: true }"
                                    class="subtitle-2"
                                ></v-select>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-card-text class="pt-0 font-weight-medium">
                                <v-row align="center">
                                    <v-divider></v-divider>
                                    <span class="pl-3 pr-3">Phases</span>
                                    <v-divider></v-divider>
                                </v-row>
                            </v-card-text>
                        </v-row>

                        <v-row
                            v-for="(phase, index) in editedItem.phases"
                            v-bind:key="index"
                        >
                            <v-col cols="3" md="2">
                                <v-text-field
                                    v-model="phase.phaseNo"
                                    label="Phase"
                                    outlined
                                    disabled
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="4"
                                :md="editGrowthTemplateInWeeks ? 2 : 3"
                                v-if="editGrowthTemplateInWeeks"
                            >
                                <v-text-field
                                    v-model="phase.numWeeks"
                                    :rules="[
                                        v => !!v || 'Required',
                                        v => v > 0 || 'Invalid value'
                                    ]"
                                    type="number"
                                    label="# Weeks*"
                                    outlined
                                    class="subtitle-2"
                                    @input="updatePhaseNumDaysByWeeks(phase)"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="4"
                                :md="editGrowthTemplateInWeeks ? 2 : 3"
                            >
                                <v-text-field
                                    v-model="phase.numDays"
                                    :rules="[rules.required]"
                                    min="1"
                                    type="number"
                                    label="# Days*"
                                    outlined
                                    :disabled="editGrowthTemplateInWeeks"
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="5" md="3">
                                <v-text-field
                                    v-model="phase.numPlantsPerM2"
                                    :rules="[
                                        v => !!v || 'Required',
                                        v => v > 0 || 'Invalid value'
                                    ]"
                                    type="number"
                                    label="# Plants Per m2*"
                                    outlined
                                    class="subtitle-2"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                :md="editGrowthTemplateInWeeks ? 3 : 4"
                            >
                                <v-autocomplete
                                    v-model="
                                        phase.defaultGreenhouseDepartmentSectionNodeId
                                    "
                                    :items="greenhouseDepartmentSections"
                                    item-value="id"
                                    item-text="fullName"
                                    :loading="
                                        $apollo.queries.greenhouses.loading
                                    "
                                    label="Greenhouse"
                                    class="subtitle-2"
                                    outlined
                                    clearable
                                >
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-5 pr-5">
                    <v-spacer></v-spacer>
                    <v-btn text rounded @click="close">Cancel</v-btn>
                    <v-btn
                        outlined
                        rounded
                        :loading="isSaving"
                        :disabled="!isFormValid"
                        color="primary"
                        @click="save"
                        >Save</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";

export default {
    name: "plant-growth-template-edit-dialog",
    props: ["dialog", "plant", "object", "isCopy", "defaultPotSizeNodeId"],

    apollo: {
        greenhouses: {
            query: gql`
                query greenhouses {
                    greenhouses {
                        edges {
                            node {
                                id
                                name
                                greenhouseDepartmentSet(
                                    isPlaceholder: false
                                    orderBy: "name"
                                ) {
                                    edges {
                                        node {
                                            id
                                            name
                                            greenhouseDepartmentSectionSet(
                                                isPlaceholder: false
                                                orderBy: "name"
                                            ) {
                                                edges {
                                                    node {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.greenhouses,
            skip() {
                return !this.dialog;
            }
            // pollInterval: 300 // ms
        }
    },

    data() {
        return {
            error: null,
            isFormValid: false,
            isLoadingPlantGrowthTemplate: false,
            isLoadingPotSizes: false,
            isLoadingGreenhouseDepartmentSections: false,
            isInitializing: false,
            isSaving: false,
            editGrowthTemplateInWeeks: false,

            // numSaleDaysChoices: [1, 2, 3, 4, 5,6,7,8,9,10,11,12,13],
            numPhasesChoices: [1, 2, 3, 4, 5],
            monthsChoices: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
            editedItem: {
                id: "",
                potSizeNodeId: "",
                name: "",
                numPhases: 1,
                seasonEffectMonths: [],
                maxSalesWeeks: null,
                maxSalesDays: 21,
                phases: [
                    {
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        defaultGreenhouseDepartmentSectionNodeId: null
                    }
                ]
            },

            greenhouses: {},

            defaultItem: {
                id: "",
                potSizeNodeId: "",
                name: "Default",
                numPhases: 1,
                seasonEffectMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                maxSalesWeeks: 3,
                maxSalesDays: 21,
                phases: [
                    {
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        defaultGreenhouseDepartmentSectionNodeId: null
                    }
                ]
            },
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            }
        };
    },
    computed: {
        // fullName: function() {
        //     const selectPotSizeNodeId = this.editedItem.potSizeNodeId;
        //     var selectedPotSize = this.potSizes.find(function(element) {
        //         return element.node.id == selectPotSizeNodeId;
        //     });

        //     var potSizeName = selectedPotSize
        //         ? selectedPotSize.node.potSizeCm + "cm"
        //         : "";
        //     return `[${potSizeName || "PotSize"}]-[${this.editedItem
        //         .seasonEffectMonths || "Months"}]-[${this.editedItem.name ||
        //         "Label"}]`;
        // },
        // numPhases: function() {
        //     return this.editedItem.numPhases;
        // },
        numSaleWeeksChoices: function() {
            return Array(10)
                .fill(0)
                .map((_, i) => i + 1);
        },
        numSaleDaysChoices: function() {
            return Array(90)
                .fill(0)
                .map((_, i) => i + 1);
        },
        sortedPotSizes: function() {
            return this.plant.plantPotSizeSet.edges
                .map(item => {
                    return { node: item.node.potSize };
                })
                .sort((a, b) => {
                    return a.node.potSizeCm - b.node.potSizeCm;
                });
        },
        // greenhouseDepartmentSections: function() {
        //     return this.$store.state.greenhouseDepartmentSections.greenhouseDepartmentSections.filter(
        //         item => {
        //             return !item.node.isPlaceholder;
        //         }
        //     );
        // },
        greenhouseDepartmentSections() {
            var _sections = [];

            if (this.greenhouses.edges) {
                for (let greenhouse of this.greenhouses.edges) {
                    if (greenhouse.node.greenhouseDepartmentSet) {
                        for (let department of greenhouse.node
                            .greenhouseDepartmentSet.edges) {
                            if (
                                department.node.greenhouseDepartmentSectionSet
                            ) {
                                for (let section of department.node
                                    .greenhouseDepartmentSectionSet.edges) {
                                    _sections.push({
                                        id: section.node.id,
                                        name: section.node.name,
                                        fullName:
                                            greenhouse.node.name +
                                            "-" +
                                            department.node.name +
                                            "-" +
                                            section.node.name
                                    });
                                }
                            }
                        }
                    }
                }
            }

            return _sections;
        },

        formTitle() {
            return this.isCopy
                ? "Copy Growth Template"
                : this.object
                ? "Edit Growth Template"
                : "New Growth Template";
        }
    },
    watch: {
        // reset form when dialog open or close
        dialog(val) {
            // if open dialog, initialize selections
            if (val) {
                this.initialize();
            }
        },
        "editedItem.numPhases"(val) {
            if (this.isInitializing) {
                return;
            }

            if (val > this.editedItem.phases.length) {
                for (var i = this.editedItem.phases.length + 1; i <= val; i++) {
                    this.editedItem.phases.push({
                        phaseNo: i,
                        numDays: "",
                        numPlantsPerM2: ""
                    });
                }
            } else if (val < this.editedItem.phases.length) {
                this.editedItem.phases.splice(
                    val,
                    this.editedItem.phases.length - val
                );
            }
        },
        editGrowthTemplateInWeeks(val) {
            if (val) {
                this.editedItem.maxSalesWeeks = Math.ceil(
                    this.editedItem.maxSalesDays / 7
                );
                this.editedItem.phases.forEach(item => {
                    item.numWeeks = Math.ceil(item.numDays / 7);
                    item.numDays = item.numWeeks * 7;
                });
            }

            localStorage.setItem("editGrowthTemplateInWeeks", val || false);
        },
        "editedItem.maxSalesWeeks"(val) {
            if (this.isInitializing) {
                return;
            }
            if (!this.editGrowthTemplateInWeeks) {
                return;
            }
            this.editedItem.maxSalesDays = val * 7;
        }
    },

    mounted() {
        const _editInWeeks = localStorage.getItem("editGrowthTemplateInWeeks");

        this.editGrowthTemplateInWeeks = JSON.parse(_editInWeeks || "false");
    },
    methods: {
        initialize() {
            this.isInitializing = true;

            this.resetForm();

            if (this.object !== null) {
                // this.editedItem.id = this.object.id;
                // // this.editedItem.name = this.isCopy ? null : this.object.name;
                // this.editedItem.name = this.object.name;
                // this.editedItem.numPhases = this.object.numPhases;
                // this.editedItem.potSizeNodeId = this.object.potSize.id;
                // this.editedItem.seasonEffectMonths = JSON.parse(
                //     this.object.seasonEffectMonths
                // );
                // this.editedItem.maxSalesDays = this.object.maxSalesDays;
                // this.editedItem.phases = this.object.plantGrowthTemplatePhaseSet.edges.map(
                //     (item, index) => {
                //         return {
                //             phaseNo: index + 1,
                //             numDays: item.node.numDays,
                //             numPlantsPerM2: item.node.numPlantsPerM2,
                //             defaultGreenhouseDepartmentSectionNodeId: item.node
                //                 .defaultGreenhouseDepartmentSection
                //                 ? item.node.defaultGreenhouseDepartmentSection
                //                       .id
                //                 : null
                //         };
                //     }
                // );
                this.getPlantGrowthTemplate(this.object.id);
            } else {
                this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));
                this.editedItem.potSizeNodeId = this.defaultPotSizeNodeId;
            }

            setTimeout(() => {
                this.isInitializing = false;
            }, 1000);
        },

        getPlantGrowthTemplate(plantGrowthTemplateNodeId) {
            if (!plantGrowthTemplateNodeId) {
                return;
            }

            this.isLoadingPlantGrowthTemplate = true;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query plantGrowthTemplate($id: ID!) {
                            plantGrowthTemplate(id: $id) {
                                id
                                name
                                numPhases
                                seasonEffectMonths
                                totalGrowthDays
                                maxSalesDays
                                potSize {
                                    id
                                    potSizeCm
                                }
                                plant {
                                    id
                                    name
                                }
                                plantGrowthTemplatePhaseSet {
                                    edges {
                                        node {
                                            id
                                            phaseNo
                                            numDays
                                            numPlantsPerM2
                                            defaultGreenhouseDepartmentSection {
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                                createdAt
                                updatedAt
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        id: plantGrowthTemplateNodeId
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result
                    // console.log(response.data);

                    let plantGrowthTemplate = response.data.plantGrowthTemplate;

                    // console.log("plantGrowthTemplate: ", plantGrowthTemplate);

                    this.editedItem.id = plantGrowthTemplate.id;
                    this.editedItem.name = this.isCopy
                        ? null
                        : plantGrowthTemplate.name;
                    this.editedItem.numPhases = plantGrowthTemplate.numPhases;
                    this.editedItem.potSizeNodeId =
                        plantGrowthTemplate.potSize.id;
                    this.editedItem.seasonEffectMonths = JSON.parse(
                        plantGrowthTemplate.seasonEffectMonths
                    );
                    this.editedItem.maxSalesDays =
                        plantGrowthTemplate.maxSalesDays;
                    this.editedItem.maxSalesWeeks = Math.ceil(
                        plantGrowthTemplate.maxSalesDays / 7
                    );
                    this.editedItem.phases = plantGrowthTemplate.plantGrowthTemplatePhaseSet.edges.map(
                        (item, index) => {
                            const _numWeeks = Math.ceil(item.node.numDays / 7);
                            // item.node.numDays = _numWeeks * 7;

                            return {
                                phaseNo: index + 1,
                                numDays: item.node.numDays,
                                numWeeks: _numWeeks,
                                numPlantsPerM2: item.node.numPlantsPerM2,
                                defaultGreenhouseDepartmentSectionNodeId: item
                                    .node.defaultGreenhouseDepartmentSection
                                    ? item.node
                                          .defaultGreenhouseDepartmentSection.id
                                    : null
                            };
                        }
                    );
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    // this.isSavingOrderPrintStatus = false;
                    this.isLoadingPlantGrowthTemplate = false;
                });
        },

        updatePhaseNumDaysByWeeks(phase) {
            phase.numDays = phase.numWeeks * 7;
        },

        resetForm() {
            // reset form state
            this.error = null;
            this.isSaving = false;

            this.editedItem = JSON.parse(JSON.stringify(this.defaultItem));

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },

        reverseSelectedMonths() {
            const currentSelectedMonths = this.editedItem.seasonEffectMonths;

            this.editedItem.seasonEffectMonths = this.monthsChoices.filter(
                item => {
                    return !currentSelectedMonths.includes(item);
                }
            );
        },

        close() {
            this.$emit("update:dialog", false);
            this.$emit("update:defaultPotSizeNodeId", null);
            this.resetForm();
        },

        save() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (this.editedItem.seasonEffectMonths.length == 0) {
                return;
            }

            // set form state
            this.error = null;
            this.isSaving = true;

            // prepare api call payload
            var payload = {
                plantNodeId: this.plant.id,
                name: this.editedItem.name,
                potSizeNodeId: this.editedItem.potSizeNodeId,
                seasonEffectMonths: this.editedItem.seasonEffectMonths.sort(
                    (a, b) => {
                        return a == b ? 0 : a > b ? 1 : -1;
                    }
                ),
                maxSalesDays: this.editedItem.maxSalesDays,
                phases: this.editedItem.phases.map(item => {
                    return {
                        phaseNo: item.phaseNo,
                        numDays: this.editGrowthTemplateInWeeks
                            ? item.numWeeks * 7
                            : item.numDays,
                        numPlantsPerM2: item.numPlantsPerM2,
                        defaultGreenhouseDepartmentSectionNodeId:
                            item.defaultGreenhouseDepartmentSectionNodeId
                    };
                })
            };

            // if editing
            if (this.object && !this.isCopy) {
                // prepare graphql mutation payload
                payload.id = this.editedItem.id;

                // update
                this.$store
                    .dispatch(
                        "plantGrowthTemplates/updatePlantGrowthTemplate",
                        payload
                    )
                    .then(response => {
                        // update parent object
                        this.$emit("item-updated", response);
                        this.close();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
            // if new
            else {
                this.$store
                    .dispatch(
                        "plantGrowthTemplates/createPlantGrowthTemplate",
                        payload
                    )
                    .then(response => {
                        this.$emit("item-created", response);
                        this.close();
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");
                        this.isSaving = false;
                    })
                    .finally(() => {});
            }
        }
    }
};
</script>